import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import Api from '../../utils/API';
import ApiErrors from '../../utils/API/APIErrors';
import {
  changeCollectionState,
  changeCommentsState,
  deleteCommentSuccess,
  errorsGlobalError,
  fetchCommentCounts,
  fetchCommentCountsSuccess,
  fetchImageCommentsSuccess,
  showNotifySuccess
} from '../../actions';
import {
  IDeleteCommentAction,
  IDisableCollectionCommentsAction,
  IFetchCommentCountsParams,
  IFetchImageCommentsParams,
  ISagaAction
} from '../../types';
import { getCollectionId } from '../../selectors';

export const fetchCommentCountsSaga = function* ({
  payload
}: ISagaAction<IFetchCommentCountsParams>): SagaIterator {
  try {
    const response = yield call(Api.Comments.counts, { collection_id: payload.collection_id });
    ApiErrors.checkOnApiError(response);

    yield put(fetchCommentCountsSuccess(response.result || {}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const fetchImageCommentsSaga = function* ({
  payload
}: ISagaAction<IFetchImageCommentsParams>): SagaIterator {
  try {
    yield put(changeCommentsState({ isFetching: true }));

    const response = yield call(Api.Comments.list, payload);
    ApiErrors.checkOnApiError(response);

    yield put(
      fetchImageCommentsSuccess({ imageId: payload.image_id, comments: response.result || [] })
    );
  } catch (e) {
    yield put(changeCommentsState({ isFetching: false }));
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const deleteCommentSaga = function* ({
  payload
}: ISagaAction<IDeleteCommentAction>): SagaIterator {
  try {
    yield put(changeCommentsState({ deletingId: payload.commentId }));

    const response = yield call(Api.Comments.remove, payload.commentId);
    ApiErrors.checkOnApiError(response);

    yield put(deleteCommentSuccess(payload));
    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(changeCommentsState({ deletingId: null }));
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

/**
 * Switching comments off from the collection settings tab. `comment` stays the
 * master on/off switch, `commentsMode` records how it was switched off so the
 * gallery knows whether to hide, keep read-only or purge existing comments.
 */
export const disableCollectionCommentsSaga = function* ({
  payload
}: ISagaAction<IDisableCollectionCommentsAction>): SagaIterator {
  try {
    const collectionID = yield select(getCollectionId);

    const modeResponse = yield call(Api.Collections.update, {
      collection_id: collectionID,
      path: 'commentsMode',
      value: payload.mode
    });
    ApiErrors.checkOnApiError(modeResponse);
    yield put(changeCollectionState({ path: 'commentsMode', value: payload.mode }));

    const commentResponse = yield call(Api.Collections.update, {
      collection_id: collectionID,
      path: 'comment',
      value: false
    });
    ApiErrors.checkOnApiError(commentResponse);
    yield put(changeCollectionState({ path: 'comment', value: false }));

    if (payload.mode === 'deleted') {
      // Comments attached to a saved selection survive the purge, so let the BE
      // decide what is left rather than blanking the badges locally.
      yield put(changeCommentsState({ threads: {} }));
      yield put(fetchCommentCounts({ collection_id: collectionID }));
    }

    yield put(showNotifySuccess({}));
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};
